import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const EnquiryDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    loadEnquiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEnquiry = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getEnquiry(id);
      
      // Debug: Log the response structure
      console.log('EnquiryDetail - API Response:', JSON.stringify(response, null, 2));
      
      // Handle both response structures: direct object or wrapped in data property
      const enquiryData = response.data || response;
      
      // Debug: Log the enquiry data
      console.log('EnquiryDetail - Enquiry Data:', JSON.stringify(enquiryData, null, 2));
      console.log('EnquiryDetail - Enquiry Data Keys:', enquiryData ? Object.keys(enquiryData) : 'null');
      
      if (!enquiryData || (typeof enquiryData === 'object' && Object.keys(enquiryData).length === 0)) {
        setError('Enquiry data is empty or invalid');
        return;
      }
      
      setEnquiry(enquiryData);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load enquiry details';
      setError(errorMessage);
      console.error('EnquiryDetail error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/enquiries');
  };

  const handleEdit = () => {
    navigate(`/enquiries/edit/${id}`);
  };

  if (loading) {
    return (
      <div className="detail-page-container">
        <div className="loading">Loading enquiry details...</div>
      </div>
    );
  }

  if (error || !enquiry) {
    return (
      <div className="detail-page-container">
        <div className="error-message">{error || 'Enquiry not found'}</div>
        <button onClick={handleBack} className="btn btn-secondary">
          Back to Enquiries
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-container">
      <div className="detail-page-header">
        <div>
          <h1>Enquiry Details</h1>
          <p className="detail-page-subtitle">
            Enquiry ID: {enquiry.id || enquiry.enquiry_id || id}
          </p>
        </div>
        <div className="detail-actions">
          <button onClick={handleBack} className="btn btn-secondary">
            Back to List
          </button>
          <button onClick={handleEdit} className="btn btn-primary">
            Edit Enquiry
          </button>
        </div>
      </div>

      <div className="detail-page-content">
        <div className="detail-section">
          <h3 className="detail-section-title">Company Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Company Name</span>
              <span className="detail-value">{enquiry.company_name || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Domain</span>
              <span className="detail-value">{enquiry.domain || '-'}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3 className="detail-section-title">Contact Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Full Name</span>
              <span className="detail-value">{enquiry.full_name || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email</span>
              <span className="detail-value">
                {enquiry.email ? (
                  <a href={`mailto:${enquiry.email}`}>{enquiry.email}</a>
                ) : '-'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Mobile</span>
              <span className="detail-value">
                {enquiry.mobile ? (
                  <a href={`tel:${enquiry.mobile}`}>{enquiry.mobile}</a>
                ) : '-'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Address</span>
              <span className="detail-value">{enquiry.address || '-'}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3 className="detail-section-title">Enquiry Details</h3>
          <div className="detail-item full-width">
            <span className="detail-label">Description</span>
            <span className="detail-value" style={{whiteSpace: 'pre-wrap'}}>
              {enquiry.enquiry_details || '-'}
            </span>
          </div>
        </div>

        {enquiry.file_link && (
          <div className="detail-section">
            <h3 className="detail-section-title">Attachments</h3>
            <div className="detail-item">
              <span className="detail-label">File</span>
              <span className="detail-value">
                <a 
                  href={enquiry.file_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-secondary"
                >
                  View Attachment
                </a>
              </span>
            </div>
          </div>
        )}

        <div className="detail-section">
          <h3 className="detail-section-title">System Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Created At</span>
              <span className="detail-value">
                {enquiry.created_at 
                  ? new Date(enquiry.created_at).toLocaleString()
                  : '-'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {enquiry.updated_at 
                  ? new Date(enquiry.updated_at).toLocaleString()
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryDetail;

